/**
 * Generato automaticamente.
 * framework-cmp-domande
 */
import { useQuery } from 'react-query';
import type {
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '@framework/api-client/client/apiClient.ts';
import type { ErrorType } from '@framework/api-client/client/apiClient.ts';
import type { AlboConfigurazioneTipoCampiDTO } from '../schemas/alboConfigurazioneTipoCampiDTO.ts';
import type { AlboConfigurazioneTipoDTO } from '../schemas/alboConfigurazioneTipoDTO.ts';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Restituisce una lista di Tipi Albo disponibili per il front-office
 * @summary Ottieni Tipi Albo FO
 */
export const getAlboCfgTipi = (
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<AlboConfigurazioneTipoDTO[]>(
    { url: `/framework-cmp-domande/albo-cfg-tipi`, method: 'get', signal },
    options,
  );
};

export const getGetAlboCfgTipiQueryKey = () =>
  [`/framework-cmp-domande/albo-cfg-tipi`] as const;

export const getGetAlboCfgTipiQueryOptions = <
  TData = Awaited<ReturnType<typeof getAlboCfgTipi>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getAlboCfgTipi>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryOptions<
  Awaited<ReturnType<typeof getAlboCfgTipi>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAlboCfgTipiQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAlboCfgTipi>>> = ({
    signal,
  }) => getAlboCfgTipi(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetAlboCfgTipiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAlboCfgTipi>>
>;
export type GetAlboCfgTipiQueryError = ErrorType<unknown>;

/**
 * @summary Ottieni Tipi Albo FO
 */
export const useGetAlboCfgTipi = <
  TData = Awaited<ReturnType<typeof getAlboCfgTipi>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getAlboCfgTipi>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetAlboCfgTipiQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Recupera i tipi campo associati al Tipo Albo
 * @summary Recupera i campi del Tipo Albo
 */
export const getAlboCfgTipiIdTipoAlboCampi = (
  idTipoAlbo: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<AlboConfigurazioneTipoCampiDTO>(
    {
      url: `/framework-cmp-domande/albo-cfg-tipi/${idTipoAlbo}/campi`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetAlboCfgTipiIdTipoAlboCampiQueryKey = (idTipoAlbo: string) =>
  [`/framework-cmp-domande/albo-cfg-tipi/${idTipoAlbo}/campi`] as const;

export const getGetAlboCfgTipiIdTipoAlboCampiQueryOptions = <
  TData = Awaited<ReturnType<typeof getAlboCfgTipiIdTipoAlboCampi>>,
  TError = ErrorType<unknown>,
>(
  idTipoAlbo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getAlboCfgTipiIdTipoAlboCampi>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getAlboCfgTipiIdTipoAlboCampi>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetAlboCfgTipiIdTipoAlboCampiQueryKey(idTipoAlbo);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getAlboCfgTipiIdTipoAlboCampi>>
  > = ({ signal }) =>
    getAlboCfgTipiIdTipoAlboCampi(idTipoAlbo, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!idTipoAlbo, ...queryOptions };
};

export type GetAlboCfgTipiIdTipoAlboCampiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAlboCfgTipiIdTipoAlboCampi>>
>;
export type GetAlboCfgTipiIdTipoAlboCampiQueryError = ErrorType<unknown>;

/**
 * @summary Recupera i campi del Tipo Albo
 */
export const useGetAlboCfgTipiIdTipoAlboCampi = <
  TData = Awaited<ReturnType<typeof getAlboCfgTipiIdTipoAlboCampi>>,
  TError = ErrorType<unknown>,
>(
  idTipoAlbo: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getAlboCfgTipiIdTipoAlboCampi>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetAlboCfgTipiIdTipoAlboCampiQueryOptions(
    idTipoAlbo,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
